import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, InputAdornment, Select, FormControl, InputLabel, MenuItem, Modal, Typography, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";
import CustomOutlinedSwitch from '../Common/CustomOutlinedSwitch';
import { getTenantToCourse } from '../../features/tenantToCourse/tenantToCourseAction';
import { useNavigate, useParams } from 'react-router-dom';

export default function CourseAccess(props) {
  const dispatch = useDispatch();
  const searchDebounceRef = useRef();
  const hasFetched = useRef(false);
  const [searchText, setSearchText] = useState("");
  const [showAllowAccess, setShowAllowAccess] = useState(false);
  const [showRevokeAccess, setShowRevokeAccess] = useState(false);
  const [pendingToggleId, setPendingToggleId] = useState(null);
  const [tenants, setTenants] = useState([]);
  const { id: tenantIdFromUrl } = useParams();
  const navigate = useNavigate();
  const tenantList = useSelector((state) => state.tenantToCourse);
  const selectedTenant = tenants.find((row) => row.id === pendingToggleId);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  useEffect(()=> {
    const courses = tenantList?.data?.courseList || [];
    const filteredData = courses.map((row) => ({
      id: row?._id,
      courseTitle: row?.sourceTenantAndCourseDetails?.courseDetails?.courseName,
      dueDate: row?.updated?.on ? moment(row.updated?.on).format("DD-MM-YYYY") : "",
      access: row?.sourceTenantAndCourseDetails?.courseDetails?.isAccessible ?? false,
    }));
    setTenants(filteredData);
  }, [tenantList])
  
  useEffect(()=> {
      if (!hasFetched.current) {
        getTenantDetailsData();
        hasFetched.current = true;
      }
  },[])

  const getTenantDetailsData = (searchValue = "") => {
      dispatch(getTenantToCourse({ search: !!searchValue, searchText: searchValue, id: selectedTenantId || tenantIdFromUrl }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      getTenantDetailsData(value);
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchText("");
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    getTenantDetailsData("");
  };

  const handleSearch = () => {
    getTenantDetailsData(searchText);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCloseModal = () => {
    setShowAllowAccess(false);
    setShowRevokeAccess(false);
    setPendingToggleId(null);
  };

  const handleAllowAccess = () => {
    setTenants((prev) =>
      prev.map((row) =>
        row.id === pendingToggleId ? { ...row, access: true } : row
      )
    );
    setShowAllowAccess(false);
    setShowAllowAccess(false);
    setPendingToggleId(null);
  }

  const handleRevokeAccess = () => {
    setTenants((prev) =>
      prev.map((row) =>
        row.id === pendingToggleId ? { ...row, access: false } : row
      )
    );
    setShowRevokeAccess(false);
    setPendingToggleId(null);
   }

  const handleToggleAccess = (id, checked) => {
    setPendingToggleId(id);
    if (checked) {
        setShowAllowAccess(true);
    } else {
        setShowRevokeAccess(true);
    }
  };

  const columns = [
    {
      field: 'courseTitle',
      headerName: 'Course Title',
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: '#1976d2',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {params.row.courseTitle}
          </Box>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due date',
      flex: 0.6,
    },
    {
      field: 'access',
      headerName: 'Access',
      flex: 0.6,
      renderCell: (params) => (
        <Box sx={{display: 'ruby', height: '20px', width: '20px'}}>
            <CustomOutlinedSwitch
                checked={params.row.access}
                onChange={(e) => handleToggleAccess(params.row.id, e.target.checked)}
            />
        </Box>
      ),
    },
  ];

  const handleTenantChange = (event) => {
    const newTenantId = event.target.value;
    setSelectedTenantId(newTenantId);
    navigate(`/courses/access/${newTenantId}`);
    dispatch(getTenantToCourse({ id: newTenantId }));
  };

  return (
    <Box sx={{ width: "100%", padding: "20px", cursor: "pointer", marginTop: "50px" }}>
        <Box sx={{ display: "flex", width: '100%', justifyContent: 'space-between'}}>
            <FormControl required 
            sx={{
                marginBottom: "16px",
                minWidth: '300px',
                "& .MuiInputLabel-asterisk": { color: "red" },
            }}
            >
            <InputLabel>Select Customer</InputLabel>
            <Select label="Select Customer" value={selectedTenantId || tenantIdFromUrl || ""} onChange={handleTenantChange}>
              {(tenantList?.data?.tenantList || []).map((tenant) => (
                <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
            </FormControl>
            <Box sx={{ width: "300px", margin: "0px 0 20px 0px" }}>
            <TextField fullWidth variant="outlined" placeholder="Search customer" value={searchText} onChange={handleSearchChange} onKeyDown={handleKeyDown} 
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                    {searchText ? (
                        <IconButton onClick={handleSearchClear}>
                        <CloseIcon />
                        </IconButton>
                    ) : (
                        <IconButton onClick={handleSearch}>
                        <SearchIcon />
                        </IconButton>
                    )}
                    </InputAdornment>
                ),
                }}
            />
            </Box>
        </Box>
      <DataGrid rows={tenants} columns={columns} disableColumnMenu disableColumnReorder disableSelectionOnClick pageSizeOptions={[20]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        sx={{
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "700",
            color: "#1976d2",
          },
        }}
      />
        {showAllowAccess && <>
            <Modal open={showAllowAccess} onClose={handleCloseModal}>
                <Box style={{ width: '550px', borderRadius: '16px', position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "white", boxShadow: 24, p: 4}}>
                <Typography variant="h6" component="h2" sx={{padding: '20px 0px', fontWeight: '600', color: '#1976d2', textAlign: 'center', borderBottom: '1px solid lightgrey', fontSize: '20px'}}>
                    Allow Access
                </Typography>
                <Box sx={{padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px'}}>
                    <Typography variant="h6" component="h2" sx={{ fontSize: '18px' , textAlign: 'center'}}>
                        Are you sure you want to allow the access to <br/>
                        <strong>{selectedTenant?.courseTitle || ""}</strong> course for this user?
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "center", gap: '20px' }}>
                    <Button variant="contained" onClick={handleCloseModal} sx={{textTransform: 'capitalize', fontWeight: '600', background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', borderRadius: '10px'}}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleAllowAccess} sx={{textTransform: 'capitalize', fontWeight: '600', borderRadius: '10px'}}>
                        Yes, Allow Access
                    </Button>
                    </Box>
                </Box>
                </Box>
            </Modal>
        </>}
        {showRevokeAccess && <>
            <Modal open={showRevokeAccess} onClose={handleCloseModal}>
                <Box style={{ width: '550px', borderRadius: '16px', position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "white", boxShadow: 24, p: 4}}>
                <Typography variant="h6" component="h2" sx={{padding: '20px 0px', fontWeight: '600', color: '#1976d2', textAlign: 'center', borderBottom: '1px solid lightgrey', fontSize: '20px'}}>
                    Revoke Access
                </Typography>
                <Box sx={{padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px'}}>
                    <Typography variant="h6" component="h2" sx={{ fontSize: '18px' , textAlign: 'center'}}>
                        Are you sure you want to revoke the access to <br/>
                        <strong>{selectedTenant?.courseTitle || ""}</strong> course for this user?
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "center", gap: '20px' }}>
                    <Button variant="contained" onClick={handleCloseModal} sx={{textTransform: 'capitalize', fontWeight: '600', background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', borderRadius: '10px'}}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleRevokeAccess} sx={{textTransform: 'capitalize', fontWeight: '600', borderRadius: '10px'}}>
                        Yes, Revoke Access
                    </Button>
                    </Box>
                </Box>
                </Box>
            </Modal>
        </>}
    </Box>
  );
}

